// src/api/servicios.js
import { apiFetch } from './index.js';

export const obtenerServicios = async (estado = 'all') => {
  try {
    let endpoint = '/Servicio';
    if (estado === 'activos') endpoint = '/Servicio/activos';
    else if (estado === 'inactivo') endpoint = '/Servicio/estado/inactivo';

    const response = await apiFetch(endpoint);
    const data = Array.isArray(response) ? response : (response.data || []);

    return {
      success: true,
      data: data.map(s => ({
        idServicio: s.idServicio || s.id || s.id_servicio,
        codigo: s.codigo,
        nombre: s.nombre,
        descripcion: s.descripcion || '',
        precioUsd: s.precioUsd || s.precio_usd || 0,
        precioBs: s.precioBs || s.precio_bs || 0,
        costoManoObraUsd: s.costoManoObraUsd || s.costo_mano_obra_usd || 0,
        comisionTecnico: s.comisionTecnico || s.comision_tecnico || 0,
        duracion: s.duracion || 0,
        garantiaDias: s.garantiaDias || s.garantia_dias || 0,
        garantiaDescripcion: s.garantiaDescripcion || s.garantia_descripcion || '',
        estado: s.estado || 'activo',
        fechaCreacion: s.fecha_creacion || s.fechaCreacion
      }))
    };
  } catch (error) {
    return { success: false, error: error.message, data: [] };
  }
};

export const verificarServicioPorCodigo = async (codigo) => {
  try {
    const response = await apiFetch(`/Servicio/codigo/${encodeURIComponent(codigo)}/exists`);
    return { success: true, existe: response?.exists || false };
  } catch (error) {
    return { success: false, existe: false };
  }
};

export const verificarServicioPorNombre = async (nombre) => {
  try {
    const response = await apiFetch(`/Servicio/nombre/${encodeURIComponent(nombre)}/exists`);
    return { success: true, existe: response?.exists || false };
  } catch (error) {
    return { success: false, existe: false };
  }
};

export const crearServicio = async (data) => {
  try {
    const response = await apiFetch('/Servicio', {
      method: 'POST',
      body: {
        codigo: data.codigo.trim(),
        nombre: data.nombre.trim(),
        descripcion: data.descripcion || null,
        precioUsd: parseFloat(data.precioUsd) || 0,
        precioBs: parseFloat(data.precioBs) || 0,
        costoManoObraUsd: parseFloat(data.costoManoObraUsd) || 0,
        comisionTecnico: parseFloat(data.comisionTecnico) || 0,
        duracion: parseInt(data.duracion) || 0,
        garantiaDias: parseInt(data.garantiaDias) || 0,
        garantiaDescripcion: data.garantiaDescripcion || null,
        estado: data.estado || 'activo'
      }
    });
    return { success: true, data: response, message: 'Servicio creado' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const actualizarServicio = async (id, data) => {
  try {
    const response = await apiFetch(`/Servicio/${id}`, {
      method: 'PUT',
      body: {
        idServicio: parseInt(id),
        codigo: data.codigo.trim(),
        nombre: data.nombre.trim(),
        descripcion: data.descripcion || null,
        precioUsd: parseFloat(data.precioUsd) || 0,
        precioBs: parseFloat(data.precioBs) || 0,
        costoManoObraUsd: parseFloat(data.costoManoObraUsd) || 0,
        comisionTecnico: parseFloat(data.comisionTecnico) || 0,
        duracion: parseInt(data.duracion) || 0,
        garantiaDias: parseInt(data.garantiaDias) || 0,
        garantiaDescripcion: data.garantiaDescripcion || null,
        estado: data.estado || 'activo'
      }
    });
    return { success: true, data: response, message: 'Servicio actualizado' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const eliminarServicio = async (id) => {
  try {
    await apiFetch(`/Servicio/${id}`, { method: 'DELETE' });
    return { success: true, message: 'Servicio eliminado' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const activarServicio = async (id) => {
  try {
    const response = await apiFetch(`/Servicio/${id}`);
    const data = response.data || response;
    return await actualizarServicio(id, { ...data, estado: 'activo' });
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const desactivarServicio = async (id) => {
  try {
    const response = await apiFetch(`/Servicio/${id}`);
    const data = response.data || response;
    return await actualizarServicio(id, { ...data, estado: 'inactivo' });
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const formatearPrecio = (precio, moneda = 'usd') => {
  if (!precio && precio !== 0) return 'N/A';
  const formato = new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return moneda === 'usd' ? `$${formato.format(precio)}` : `Bs. ${formato.format(precio)}`;
};

export const formatearDuracion = (minutos) => {
  if (!minutos) return 'N/A';
  const horas = Math.floor(minutos / 60);
  const mins = minutos % 60;
  if (horas > 0 && mins > 0) return `${horas}h ${mins}min`;
  if (horas > 0) return `${horas} hora${horas !== 1 ? 's' : ''}`;
  return `${mins} minuto${mins !== 1 ? 's' : ''}`;
};

export const formatearGarantia = (dias) => {
  if (!dias) return 'Sin garantía';
  if (dias >= 365) return `${Math.floor(dias / 365)} año${Math.floor(dias / 365) !== 1 ? 's' : ''}`;
  if (dias >= 30) return `${Math.floor(dias / 30)} mes${Math.floor(dias / 30) !== 1 ? 'es' : ''}`;
  return `${dias} día${dias !== 1 ? 's' : ''}`;
};